/*
 *  Copyright (C) 2024 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Logger } from './common/log/logger'
import { getVersion, getPackageNameName } from './common/version'
import {
    checkToken,
    checkTokenExpiry,
    decodeToken,
    handleJSON,
    makeRequest,
    parseParams,
    TOKEN
} from "./common"
import {
    login,
    me,
    register,
    requestAccessToken,
    verifyToken,
} from "./account"
import {
    autocompleteAddress,
    searchAddress,
    getAddress,
    getAddressList,
    updateAddress,
    createAddress,
    deleteAddress, 
} from "./address"
import {
    publishLive,
    unpublishLive,
    publishPreview,
    unpublishPreview,
    getAlert,
    getCurrentAlert,
    getCurrentAlertForModel,
    findCurrentPreviewAlertForModel,
    getCurrentPreviewAlert,
} from "./alert"
import {
    createApplication,
    deleteApplication,
    getApplication,
    getApplications,
    searchApplications,
    updateApplication,
} from "./application"
import {
    getAttachments,
    searchAttachments,
    getAttachment,
    getAttachmentByFilePath,
    generateAttachmentSasTokenPublic,
    // These are public - does admin need them?
    createAttachmentPublic,
    deleteAttachmentPublic,
} from './attachment'
import {
    getComponentConfigList,
    getComponentConfigByType,
    getComponentConfigByCategory,
    getComponentCategoryList, getComponentConfigByAllCategory,
} from "./component-config"
import {
    createComponent,
    deleteComponent,
    getComponent,
    getComponentById,
    getComponentUsage,
    getReusableComponents,
    searchComponentsByTerm,
    updateComponent,
} from "./components"

import {
    getDocuments,
    searchDocuments,
    getDocument,
    getDocumentByFilePath,
    createDocument,
    updateDocument,
    deleteDocument,
    renameDocument,
    archiveDocument,
    purgeDocuments,
    purgeAllDocuments,
    getDocumentPlacements,
    fileExist as documentFileExist,
} from './document'
import {
    getExcerpts,
    // searchExcerpts,
    getExcerpt,
    createExcerpt,
    updateExcerpt,
    deleteExcerpt,
} from "./excerpt"
import {
    getSchemaForm,
    getSchemaForms,
    getSchemaFormKeywords,
    createSchemaForm,
    duplicateSchemaForm,
    updateSchemaForm,
    deleteSchemaForm,
    searchSchemaForms,
    getFormPlacements,
} from './form'
import {
    createForm,
    getFormResults,
    getFormResult,
    getAllFormResults,
    getFormResultsAsCSV,
    deleteFormResponse,
} from "./form-response"
import {
    getFormSetting,
    getFormSettings,
    createFormSettings,
    createFormSetting,
    updateFormSettings,
    updateFormSetting,
    deleteFormSetting,
} from "./form-settings"
import {
    getIdentityUsers,
    getIdentityUser,
    searchIdentityUsers,
} from './identity-b2c'
import {
    getImage,
    getImageExists,
    getImageByFilePath,
    getImages,
    searchImages,
    createImage,
    updateImage,
    deleteImage,
    renameImage,
    archiveImage,
    purgeAllImages,
    purgeImages,
    getImagePlacements,
    fileExist as imageFileExist,
} from "./image"
import {
    createKeyword,
    getAllKeywords,
    getKeywordsByGuid,
    deleteKeyword,
    updateKeyword,
    searchKeywords,
} from "./keyword"
import {
    createLayout,
    deleteLayout,
    getLayout,
    getLayouts,
    getLayoutsForModel,
    // searchLayouts,
    updateLayout,
} from "./layout"
import {
    getLink,
    getLinks,
    searchLinks,
    createLink,
    deleteLink,
    updateLink,
} from "./link"
import {
    generateDocumentSasToken,
    generateImageSasToken,
} from "./media"
import {
    getEmailTemplates,
    getEmailTemplate,
    createEmailTemplate,
    deleteEmailTemplate,
    updateEmailTemplate,
} from './message-template'
import {
    searchModels,
    getModels,
    getModel,
    createModel,
    deleteModel,
    updateModel,
    getRecentModelsFeed,
    getRecentEventModelsFeed,
    getModelTOC,
} from './model'
import {
    getModelFeatures,
    getModelFeaturesByModel,
    getModelFeatureDefaultsByModelType,
    getModelFeature,
    createModelFeature,
    deleteModelFeature,
    updateModelFeature,
} from './model_feature'
import {
    createOrganization,
    deleteOrganization,
    getOrganization,
    getOrganizations,
    searchOrganizations,
    updateOrganization,
} from "./organization"
import {
    getOrgSettings,
    getOrgSetting,
    createOrgSetting,
    updateOrgSetting,
    deleteOrgSettings,
} from './organization-settings'
import {
    getPermit,
    searchPermit,
    searchPermitNumber,
    myPermits
} from './services/permits'
import {
    getPermission,
    getPermissions,
    getRolesForPermission,
    createPermission,
    updatePermission,
    deletePermission,
    getRolesForPermissionAll,
} from './permission'
import {
    addResourceToQueue,
    createChangeSet,
    createVersion,
    editChange,
    getChange,
    getChangeSet,
    getNamedChangeSet,
    getModifications,
    getMyModifications,
    getStatus,
    listActiveChangeSets,
    listChangeSets,
    listCompletedChangeSets,
    listPublishHistory,
    listResourceDependencies,
    moveResourceToQueue,
    publishChangeSet,
    publishNamedQueue,
    publishNow,
    removeResourceFromQueue,
} from './publishing'
import {
    createRedirect,
    deleteRedirect,
    getRedirect,
    getRedirects,
    updateRedirect,
} from "./redirects"
import {
    createRole,
    deleteRole,
    getRole,
    getRoles,
    searchRoles,
    updateRole,
    getRolesForProfile,
} from './role'
import {
    getSchemaModels,
    getSchemaModel,
    getSchemaModelByType,
    createSchemaModel,
    addFieldToSchemaModel,
    searchSchemaModel,
    updateSchemaModel,
    updateSchemaModelTOC,
    deleteSchemaModel,
} from "./schema-model"
import {
    reindexWebpage,
    reindexWebpages,
} from "./search"
import {
    createSeo,
    deleteSeo,
    getSeo,
    getSeoByGuid,
    getSeoByWebpageGuid,
    updateSeo,
} from "./seo"
import {
    createTarget,
    deleteTarget,
    getTarget,
    getTargets,
    updateTarget,
} from "./target"
import {
    createUser,
    deleteUser,
    getUser,
    getUsers,
    searchUsers,
    updateUser,
    setUserRole,
} from "./users"
import {
    getWebpageVersionList,
    getWebpageVersion,
    searchWebpageVersion,
    getWebpageVersionLatest,
    createWebpageVersion,
    updateWebpageVersion,
    deleteWebpageVersion,
    getWebpagePackageVersion,
    restoreWebpageVersion,
    favouriteWebpageVersion, 
    updateNameWebpageVersion
} from "./version/webpage"
import {
    getModelVersionList,
    getModelVersion,
    searchModelVersion,
    getModelVersionLatest,
    createModelVersion,
    updateModelVersion,
    deleteModelVersion,
} from "./version/model"
import {
    getComponentVersionList,
    getComponentVersion,
    searchComponentVersion,
    createComponentVersion,
    updateComponentVersion,
    deleteComponentVersion,
} from "./version/component"
import {
    createWebpage,
    deleteWebpage,
    getUnparentedWebpages,
    getWebpage,
    getWebpageByPath,
    getWebpageByPathComplete,
    getWebpageBySlug,
    getWebpageChildren,
    getWebpageParents,
    getWebpages,
    getWebpageTree,
    searchWebpages,
    updateWebpage,
    reorderChildren,
    getWebpageKeywords,
    getWebpageKeywordsByWebpage,
} from "./webpage"
import {
    createWebsite,
    deleteWebsite,
    getWebsite,
    getWebsiteForApplication,
    getWebsites,
    getWebsitesForOrganization,
    getWebsiteForDomain,
    searchWebsites,
    updateWebsite,
} from "./website"

import {
    getProjects,
    getProject,
    getTopics,
    getTopic,
} from "./services/zencity";

// ==========================================================//

const version = getVersion()
const name = getPackageNameName()

const Account = {
    requestAccessToken,
    login,
    me,
    register,
    verifyToken,
}
const Alert = {
    findOne: getAlert,
    findCurrent: getCurrentAlert,
    findCurrentPreview: getCurrentPreviewAlert,
    findCurrentForModel: getCurrentAlertForModel,
    findCurrentPreviewForModel: findCurrentPreviewAlertForModel,
    publishLive: publishLive,
    unpublishLive: unpublishLive,
    publishPreview: publishPreview,
    unpublishPreview: unpublishPreview,
}
const Address = {
    autocompleteAddress,
    searchAddress,
    getAddress,
    getAddressList,
    updateAddress,
    createAddress,
    deleteAddress,
}
const Application = {
    find: getApplications,
    findOne: getApplication,
    searchApplications: searchApplications,
    create: createApplication,
    delete: deleteApplication,
    update: updateApplication,
}
const Attachment = {
    getToken: generateAttachmentSasTokenPublic,
    find: getAttachments,
    search: searchAttachments,
    findOne: getAttachment,
    findOneByFilePath: getAttachmentByFilePath,
    create: createAttachmentPublic,
    delete: deleteAttachmentPublic,
}
const Component = {
    find: getComponent,
    findOne: getComponentById,
    create: createComponent,
    delete: deleteComponent,
    update: updateComponent,
    searchComponentsByTerm: searchComponentsByTerm,
    findReusableComponents: getReusableComponents,
    getComponentUsage: getComponentUsage,
}
const ComponentConfig = {
    find: getComponentConfigList,
    findByType: getComponentConfigByType,
    findByCategory: getComponentConfigByCategory,
    findCategoryList: getComponentCategoryList,
    findAll: getComponentConfigByAllCategory
}
const Document = {
    getToken: generateDocumentSasToken,
    find: getDocuments,
    search: searchDocuments,
    findOne: getDocument,
    findOneByFilePath: getDocumentByFilePath,
    create: createDocument,
    update: updateDocument,
    delete: deleteDocument,
    rename: renameDocument,
    archive: archiveDocument,
    purge: purgeDocuments,
    purgeAll: purgeAllDocuments,
    placements: getDocumentPlacements,
    fileExist: documentFileExist,
}
const Excerpt = {
    findOne: getExcerpt,
    find: getExcerpts,
    update: updateExcerpt,
    delete: deleteExcerpt,
    create: createExcerpt,
}
const Form = {
    findOne: getSchemaForm,
    find: getSchemaForms,
    update: updateSchemaForm,
    create: createSchemaForm,
    delete: deleteSchemaForm,
    duplicate: duplicateSchemaForm,
    findKeywords: getSchemaFormKeywords,
    search: searchSchemaForms,
    findPlacements: getFormPlacements
}
const FormResponse = {
    findOne: getFormResult,
    find: getAllFormResults,
    findResultsByForm: getFormResults,
    findResultsByFormAsCSV: getFormResultsAsCSV,
    create: createForm,
    delete: deleteFormResponse,
}
const FormSetting = {
    findOne: getFormSetting,
    find: getFormSettings,
    create: createFormSettings,
    createOne: createFormSetting,
    update: updateFormSettings,
    updateOne: updateFormSetting,
    delete: deleteFormSetting,
}
const Identity = {
    find: getIdentityUsers,
    findOne: getIdentityUser,
    search: searchIdentityUsers,
}
const Image = {
    getToken: generateImageSasToken,
    find: getImages,
    search: searchImages,
    searchImages: searchImages,
    findOne: getImage,
    exists: getImageExists,
    findOneByFilePath: getImageByFilePath,
    create: createImage,
    update: updateImage,
    delete: deleteImage,
    rename: renameImage,
    archive: archiveImage,
    purge: purgeImages,
    purgeAll: purgeAllImages,
    placements: getImagePlacements,
    fileExist: imageFileExist,
}
const Layout = {
    find: getLayouts,
    findForModel: getLayoutsForModel,
    findOne: getLayout,
    // searchApplications: searchLayouts,
    create: createLayout,
    delete: deleteLayout,
    update: updateLayout,
}
const Link = {
    findOne: getLink,
    find: getLinks,
    search: searchLinks,
    create: createLink,
    delete: deleteLink,
    update: updateLink,
}
const Keyword = {
    create: createKeyword,
    find: getAllKeywords,
    findByGuid: getKeywordsByGuid,
    delete: deleteKeyword,
    update: updateKeyword,
    search: searchKeywords,
}
const Model = {
    search: searchModels,
    find: getModels,
    findOne: getModel,
    create: createModel,
    delete: deleteModel,
    update: updateModel,
    findFeed: getRecentModelsFeed,
    findEventFeed: getRecentEventModelsFeed,
    toc: getModelTOC,
}
const Messaging = {
    find: getEmailTemplates,
    findOne: getEmailTemplate,
    create: createEmailTemplate,
    delete: deleteEmailTemplate,
    update: updateEmailTemplate,
}
const ModelFeature = {
    find: getModelFeatures,
    findByModel: getModelFeaturesByModel,
    findByModelFeature: getModelFeature,
    create: createModelFeature,
    delete: deleteModelFeature,
    update: updateModelFeature,
    findDefaults: getModelFeatureDefaultsByModelType,
}
const ModelSchema = {
    find: getSchemaModels,
    findOne: getSchemaModel,
    findOneByType: getSchemaModelByType,
    search: searchSchemaModel,
    addField: addFieldToSchemaModel,
    create: createSchemaModel,
    update: updateSchemaModel,
    updateTOC: updateSchemaModelTOC,
    delete: deleteSchemaModel,
}
const Organization = {
    find: getOrganizations,
    findOne: getOrganization,
    search: searchOrganizations,
    create: createOrganization,
    delete: deleteOrganization,
    update: updateOrganization,
}
const OrganizationSetting = {
    find: getOrgSettings,
    findOne: getOrgSetting,
    create: createOrgSetting,
    delete: deleteOrgSettings,
    update: updateOrgSetting,
}
const Permission = {
    find: getPermissions,
    findOne: getPermission,
    findRoles: getRolesForPermission,
    findRolesForPermission: getRolesForPermissionAll,
    create: createPermission,
    update: updatePermission,
    delete: deletePermission,
}
const Publishing = {
    add: addResourceToQueue,
    createChangeSet,
    editChange,
    getChange,
    getChangeSet,
    getNamedChangeSet,
    getModifications,
    getMyModifications,
    getStatus,
    listActiveChangeSets,
    listChangeSets,
    listCompletedChangeSets,
    listResourceDependencies,
    listPublishHistory,
    moveResourceToQueue,
    publishChangeSet,
    publishNow,
    publishNamedQueue,
    createVersion,
    remove: removeResourceFromQueue,
}
const Redirect = {
    find: getRedirects,
    findOne: getRedirect,
    create: createRedirect,
    update: updateRedirect,
    delete: deleteRedirect,
}
const Role = {
    find: getRoles,
    findOne: getRole,
    search: searchRoles,
    create: createRole,
    update: updateRole,
    delete: deleteRole,
    findRolesForProfile: getRolesForProfile,
}
const Search = {
    reindexWebpage: reindexWebpage,
    reindexWebpages: reindexWebpages,
}
const Seo = {
    find: getSeo,
    findOne: getSeoByGuid,
    findOneByWebpage: getSeoByWebpageGuid,
    create: createSeo,
    update: updateSeo,
    delete: deleteSeo,
}
const Target = {
    find: getTargets,
    findOne: getTarget,
    create: createTarget,
    update: updateTarget,
    delete: deleteTarget,
}
const User = {
    find: getUsers,
    findOne: getUser,
    search: searchUsers,
    create: createUser,
    update: updateUser,
    delete: deleteUser,
    setRole: setUserRole,
}
const Utils = {
    makeRequest,
    decodeToken,
    checkToken,
    checkTokenExpiry,
    parseParams,
    handleJSON,
    TOKEN,
}
const Versions = {
    webpage: {
        find: getWebpageVersionList,
        findOne: getWebpageVersion,
        getVersionPackage: getWebpagePackageVersion,
        search: searchWebpageVersion,
        latest: getWebpageVersionLatest,
        create: createWebpageVersion,
        update: updateWebpageVersion,
        delete: deleteWebpageVersion,
        restore: restoreWebpageVersion,
        favourite: favouriteWebpageVersion,
        updateName: updateNameWebpageVersion
    },
    model: {
        find: getModelVersionList,
        findOne: getModelVersion,
        search: searchModelVersion,
        latest: getModelVersionLatest,
        create: createModelVersion,
        update: updateModelVersion,
        delete: deleteModelVersion,
    },
    // component: {
    //     find: getComponentVersionList,
    //     findOne: getComponentVersion,
    //     search: searchComponentVersion,
    //     create: createComponentVersion,
    //     update: updateComponentVersion,
    //     delete: deleteComponentVersion,
    // }
}
const Webpage = {
    find: getWebpages,
    search: searchWebpages,
    findOne: getWebpage,
    findOneBySlug: getWebpageBySlug,
    findOneByPath: getWebpageByPath,
    findOneExpanded: getWebpageByPathComplete,
    findTree: getWebpageTree,
    findParents: getWebpageParents,
    findChildren: getWebpageChildren,
    findUnparented: getUnparentedWebpages,
    create: createWebpage,
    delete: deleteWebpage,
    update: updateWebpage,
    reorderChildren: reorderChildren,
    getKeywords: getWebpageKeywords,
    getKeywordsByWebpage: getWebpageKeywordsByWebpage,
}
const Website = {
    find: getWebsites,
    findOne: getWebsite,
    findForOrganization: getWebsitesForOrganization,
    findForApplication: getWebsiteForApplication,
    findForDomain: getWebsiteForDomain,
    create: createWebsite,
    update: updateWebsite,
    delete: deleteWebsite,
    search: searchWebsites,
}
const check = function () {
    Logger.debug("-----------------------------------------------------")
    Logger.debug("      SimpliCitySDK (Admin) located!")
    Logger.debug(`      ${name}`)
    Logger.debug("-----------------------------------------------------")
    return true
}
const Services = {
    permit: {
        getPermit,
        searchPermit,
        searchPermitNumber,
        myPermits
    },
    zencity: {
        getProjects,
        getProject,
        getTopics,
        getTopic,
    }
}

const SimpliCityAdminSDK = {
    check,
    version,
    account: Account,
    address: Address,
    alert: Alert,
    application: Application,
    attachment: Attachment,
    componentConfig: ComponentConfig,
    component: Component,
    documents: Document,
    excerpt: Excerpt,
    form: Form,
    formResponse: FormResponse,
    formSettings: FormSetting,
    identity: Identity,
    images: Image,
    layout: Layout,
    link: Link,
    messaging: Messaging,
    model: Model,
    modelFeatures: ModelFeature,
    organization: Organization,
    orgSettings: OrganizationSetting,
    permissions: Permission,
    publishing: Publishing,
    redirects: Redirect,
    role: Role,
    schemaModel: ModelSchema,
    search: Search,
    seo: Seo,
    keyword: Keyword,
    target: Target,
    users: User,
    utils: Utils,
    webpage: Webpage,
    website: Website,
    services: Services,
    versions: Versions,
}

export { SimpliCityAdminSDK };
export { SimpliCityAdminSDK as default };
